import { useState } from 'react';
import { Button } from '@/components/Button/Button';
import { t } from '@/lib/i18n';
import {
  useLlmCatalog,
  useLlmKeys,
  useCreateLlmKey,
  useUpdateLlmKey,
  useDeleteLlmKey,
  useTestLlmKey,
} from '../hooks';
import { LlmKeyCard } from './LlmKeyCard';
import { LlmKeyFormModal } from './LlmKeyFormModal';
import type { LlmKeyRow } from '../schemas';
import styles from './LlmKeysSection.module.css';

type ModalState = { kind: 'closed' } | { kind: 'add' } | { kind: 'edit'; row: LlmKeyRow };

export function LlmKeysSection() {
  const catalog = useLlmCatalog();
  const keys = useLlmKeys();
  const create = useCreateLlmKey();
  const update = useUpdateLlmKey();
  const remove = useDeleteLlmKey();
  const test = useTestLlmKey();
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  if (catalog.isLoading || keys.isLoading) {
    return <p className={styles.muted}>{t('Loading…')}</p>;
  }
  if (catalog.isError || keys.isError) {
    return <p className={styles.error}>{t("Couldn't load LLM keys. Try refreshing.")}</p>;
  }
  if (!catalog.data || !keys.data) return null;

  const items = keys.data.items;

  async function handleSaveCreate(body: Parameters<typeof create.mutateAsync>[0]) {
    setBannerError(null);
    try {
      await create.mutateAsync(body);
      setModal({ kind: 'closed' });
    } catch (err) {
      setBannerError(err instanceof Error ? err.message : 'Failed to create key.');
    }
  }

  async function handleSaveEdit(
    id: string,
    body: Parameters<typeof update.mutateAsync>[0]['body'],
  ) {
    setBannerError(null);
    try {
      await update.mutateAsync({ id, body });
      setModal({ kind: 'closed' });
    } catch (err) {
      setBannerError(err instanceof Error ? err.message : 'Failed to update key.');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t('Delete this key? This cannot be undone.'))) return;
    setBannerError(null);
    setPendingId(id);
    try {
      await remove.mutateAsync(id);
    } catch (err) {
      setBannerError(err instanceof Error ? err.message : 'Failed to delete key.');
    } finally {
      setPendingId(null);
    }
  }

  async function handleMakeDefault(id: string) {
    setBannerError(null);
    setPendingId(id);
    try {
      await update.mutateAsync({ id, body: { makeDefault: true } });
    } catch (err) {
      setBannerError(err instanceof Error ? err.message : 'Failed to set default.');
    } finally {
      setPendingId(null);
    }
  }

  async function handleTestStored(id: string) {
    setBannerError(null);
    try {
      const result = await test.mutateAsync({ id });
      if (result.ok) {
        setBannerError(`${t('Connected')} (${result.latencyMs} ms)`);
      } else {
        setBannerError(result.error.message);
      }
    } catch (err) {
      setBannerError(err instanceof Error ? err.message : 'Test connection failed.');
    }
  }

  return (
    <section className={styles.wrap}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>{t('LLM Keys')}</h2>
          <p className={styles.subtitle}>
            {t('Manage the API keys your team uses to run AI work.')}
          </p>
        </div>
        <Button variant="primary" size="md" onClick={() => setModal({ kind: 'add' })}>
          {t('+ Add key')}
        </Button>
      </header>

      {bannerError && <div className={styles.banner}>{bannerError}</div>}

      {items.length === 0 ? (
        <div className={styles.empty}>
          <p>
            {t('No LLM keys yet. Add one to let your team run jobs against your provider account.')}
          </p>
          <Button variant="primary" size="md" onClick={() => setModal({ kind: 'add' })}>
            {t('+ Add your first key')}
          </Button>
        </div>
      ) : (
        <ul className={styles.list}>
          {items.map((row) => (
            <li key={row.id}>
              <LlmKeyCard
                keyRow={row}
                onEdit={(id) => {
                  const found = items.find((x) => x.id === id);
                  if (found) setModal({ kind: 'edit', row: found });
                }}
                onTest={(id) => void handleTestStored(id)}
                onMakeDefault={(id) => void handleMakeDefault(id)}
                onDelete={(id) => void handleDelete(id)}
                busy={pendingId === row.id}
              />
            </li>
          ))}
        </ul>
      )}

      {modal.kind === 'add' && (
        <LlmKeyFormModal
          mode="add"
          catalog={catalog.data}
          onClose={() => setModal({ kind: 'closed' })}
          onSave={(body) => void handleSaveCreate(body)}
          onTest={(input) => test.mutateAsync(input)}
          saving={create.isPending}
        />
      )}
      {modal.kind === 'edit' && (
        <LlmKeyFormModal
          mode="edit"
          catalog={catalog.data}
          initial={modal.row}
          onClose={() => setModal({ kind: 'closed' })}
          onSave={(body) => void handleSaveEdit(modal.row.id, body)}
          onTest={(input) => test.mutateAsync(input)}
          saving={update.isPending}
        />
      )}
    </section>
  );
}
